import type { SalesLineItemBadge } from '@/components/organisms/sales/SalesLineItemsReadOnlyTable';

/**
 * Badges shown next to a line item on the internal estimate detail.
 *
 * Optional items render INLINE in the main table (rather than a segregated
 * "Optional items not included" card), so the badges carry the meaning the
 * separate card used to convey:
 *  - "Optional" tags an optional line while it is still a proposal; accepted
 *    documents say "Accepted option" so the operator sees the final decision.
 *    Selected options are primary-tinted, muted otherwise.
 *  - "Not in total" is added for non-selected optional lines so an internal
 *    reader doesn't misread the row's price as counted toward the estimate.
 *  - "Upsell" flags upsell lines independently.
 *
 * Extracted as a pure function so the badge matrix is unit-testable.
 */
export function lineItemBadges(
  li: {
    isOptional?: boolean;
    isSelected?: boolean;
    isUpsell?: boolean;
    isServiceCallFeeCredit?: boolean;
  },
  acceptedScope = false,
): SalesLineItemBadge[] {
  const badges: SalesLineItemBadge[] = [];

  // NOT-560 / SPEC D3 — negative display line crediting the service-call
  // visit fee (CREDIT_ON_ESTIMATE policy). Tagged so a reader knows the
  // negative amount is the diagnostic-fee credit, not a discount.
  if (li.isServiceCallFeeCredit) {
    badges.push({
      label: 'Visit fee credit',
      className: 'bg-success/10 text-success border-success/30',
    });
  }

  if (li.isOptional) {
    // Backend approval/reprice treats a missing flag as selected
    // (`?? true` / `!== false` throughout estimates.service.ts) — match that
    // here so a legacy optional line with no isSelected still reads as
    // counted, consistent with the financial summary.
    const selected = li.isSelected !== false;
    badges.push({
      label: acceptedScope && selected ? 'Accepted option' : 'Optional',
      className: selected
        ? 'bg-primary/10 text-primary border-primary/30'
        : 'bg-muted text-muted-foreground border-border',
    });
    if (!selected) {
      badges.push({
        label: 'Not in total',
        className: 'bg-muted text-muted-foreground border-border',
      });
    }
  }

  if (li.isUpsell) {
    badges.push({
      label: 'Upsell',
      className: 'bg-warning/10 text-warning border-warning/30',
    });
  }

  return badges;
}
